import React, { useState, useEffect } from 'react';
import { Calendar, Package, Tag, MessageCircle, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ratingService } from '../../../services/ratingService';

const TYPE_CONFIG = {
  kupovina: { label: 'Kupovina', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  prodaja: { label: 'Prodaja', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  razmena: { label: 'Razmena', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d?.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatPrice(price, currency) {
  if (price === null || price === undefined) return 'Po dogovoru';
  return `${Number(price)?.toLocaleString('sr-RS')} ${currency || 'RSD'}`;
}

function MiniStarRating({ average, count }) {
  if (!average) return null;
  return (
    <div className="flex items-center gap-1 mt-1">
      {[1, 2, 3, 4, 5]?.map(star => (
        <Star
          key={star}
          size={11}
          className={star <= Math.round(average) ? 'fill-current' : 'text-gray-300'}
          style={star <= Math.round(average) ? {color:'#CFB53B'} : {}}
        />
      ))}
      <span className="text-xs text-gray-500 ml-0.5">{Number(average)?.toFixed(1)}</span>
      {count > 0 && <span className="text-xs text-gray-400">({count})</span>}
    </div>
  );
}

export default function OfferCard({ offer, hasActiveNegotiation }) {
  const navigate = useNavigate();
  const typeConf = TYPE_CONFIG?.[offer?.wishType] || TYPE_CONFIG?.prodaja;
  const [sellerRating, setSellerRating] = useState({ average: null, count: 0 });

  useEffect(() => {
    if (offer?.userId) {
      ratingService?.getUserRatingStats(offer?.userId)?.then(stats => setSellerRating(stats))?.catch(() => {});
    }
  }, [offer?.userId]);

  const handleClick = () => {
    navigate(`/offer-details-detalji-oglasa/${offer?.id}`, { state: { offer } });
  };

  return (
    <div
      onClick={handleClick}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer group"
    >
      {/* Negotiation in progress badge */}
      {hasActiveNegotiation && (
        <div className="flex items-center gap-1.5 mb-2 px-2.5 py-1 rounded-lg w-fit" style={{backgroundColor:'#FAF5DC', border:'1px solid #F5EBB9'}}>
          <MessageCircle size={13} style={{color:'#CFB53B'}} />
          <span className="text-xs font-medium" style={{color:'#9A8226'}}>Pregovaranje u toku</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate group-hover:text-teal-700 transition-colors">
            {offer?.voucherName || 'Nepoznat vaučer'}
          </h3>
          {offer?.voucherSymbol && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500 mt-0.5">
              <Tag size={11} />
              {offer?.voucherSymbol}
            </span>
          )}
          <MiniStarRating average={sellerRating?.average} count={sellerRating?.count} />
        </div>
        <span className={`flex-shrink-0 px-2 py-1 rounded-full text-xs font-medium border ${typeConf?.bg} ${typeConf?.text} ${typeConf?.border}`}>
          {typeConf?.label}
        </span>
      </div>
      {/* Logo if available */}
      {offer?.logoUrl && (
        <div className="mb-3">
          <img
            src={offer?.logoUrl}
            alt={`${offer?.voucherName} logo`}
            className="w-10 h-10 object-contain rounded border border-gray-100"
          />
        </div>
      )}
      {/* Details */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-sm text-gray-600">
            <Package size={14} className="text-gray-400" />
            <span>Količina:</span>
          </div>
          <span className="text-sm font-medium text-gray-900">
            {offer?.tokenQuantity ? offer?.tokenQuantity?.toLocaleString('sr-RS') : 'N/A'}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Cena:</span>
          <span className="text-sm font-semibold text-teal-700">
            {formatPrice(offer?.maxPrice, offer?.currency)}
          </span>
        </div>
      </div>
      {/* Description preview */}
      {offer?.description && (
        <p className="mt-3 text-xs text-gray-500 line-clamp-2">{offer?.description}</p>
      )}
      {/* Footer */}
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-1 text-xs text-gray-400">
          <Calendar size={12} />
          {formatDate(offer?.createdAt)}
        </div>
        {offer?.expiryDate && (
          <span className="text-xs text-orange-500">
            Ističe: {formatDate(offer?.expiryDate)}
          </span>
        )}
      </div>
      {/* Click hint */}
      <div className="mt-2 text-xs text-teal-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
        Kliknite za detalje →
      </div>
    </div>
  );
}
